<template>
  <section class="games-slider-group section-gradient-wrapper">
    <div class="full-width">
      <div class="flex items-center">
        <img v-if="image || titleIcon" class="categories-img" :src="image || titleIcon" />
        <span class="section__title ol-mr-auto">{{ title }}</span>
        <app-link v-if="hasAllLink" :to="showAllLink" class="section__link" @click="scrollToTop">
          {{ $t('all') }}
        </app-link>
        <ol-slider-button
          class="ol-ml-md"
          :disabled-left="progress <= 0"
          :disabled-right="progress >= 1 || countSlides <= 1"
          @click-left="prevSlide"
          @click-right="nextSlide"
        />
      </div>
    </div>

    <div class="games-slider">
      <games-list-skeleton
        v-if="!swiperReady || loadingInit"
        class="games-list-skeleton"
        :count="countForSkeleton"
        :columns="columns"
      />
      <swiper
        v-if="!loadingInit"
        :key="isDesktop"
        class="groups"
        v-bind="swiperOptionComputed"
        @progress="onProgress"
        @swiper="onSwiper"
        @init="swiperReady = true"
        @active-index-change="loadGames"
      >
        <swiper-slide
          v-for="(item, idx) in gamesFiltered"
          :key="`${title}-idx${idx}`"
          class="swiper-slide game-col"
          :class="{
            'game-col--promo': hasPromoSlot && idx === 0,
            'game-col--group': hasPromoSlot,
          }"
        >
          <template v-if="hasPromoSlot">
            <logo-game
              v-for="(game, id) in item"
              :key="`${title}-${game.id}-idx${id}`"
              :promo-game="id === 0 && idx === 0"
              :item="game"
              :logo-subgroup="logoSubgroup"
              :logo-group="group"
              class="game-col"
            />
          </template>
          <logo-game v-else :item="item" :logo-subgroup="logoSubgroup" :logo-group="group" />
        </swiper-slide>
      </swiper>
    </div>
  </section>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Grid, FreeMode } from 'swiper/modules'
import { useGamesList } from 'src/hooks/gamesList'
import { useRoute } from 'vue-router'
import GamesListSkeleton from 'components/games/GamesListSkeleton.vue'
import LogoGame from 'components/games/LogoGame.vue'
import { OlSliderButton } from '@gamesite/olimp-core'
import { BASE_URL } from 'src/common/config'

const route = useRoute()

const props = defineProps({
  groupProp: {
    type: String,
    default: '',
  },
  subgroupCode: {
    type: String,
    default: '',
  },
  subgroup: {
    type: Object,
    default: null,
  },
  allLink: {
    type: Object,
    default: null,
  },
  titleProp: {
    type: String,
    default: '',
  },
  maxRow: {
    type: Number,
    default: null,
  },
  columns: {
    type: Number,
    default: 6,
  },
  hasAllLink: {
    type: Boolean,
    default: true,
  },
  titleIcon: {
    type: [String, Object],
    default: '',
  },
  hideble: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['loaded'])
const hasPromoSlot = computed(() => {
  const key = `${route.params.group}/${props.subgroup?.code}`
  return [
    'fast/hot',
    'online/hot',
    'lottery/hot',
    'online/blackjack_live',
    'online/baccarat_live',
    'online/roulette_live',
    'online/game_shows',
    'online/asian_games',
  ].includes(key)
})

const group = computed(() => {
  if (props.groupProp) {
    if (props.groupProp === 'home-hot') {
      return 'slot'
    }

    return props.groupProp
  }
  return route.params.group
})
const subgroupCodeResult = computed(() => {
  return props.subgroupCode || props.subgroup?.slug
})

const payload = computed(() => {
  const result = {}
  if (group.value) {
    result.group = group.value
  }
  if (subgroupCodeResult.value) {
    result.subgroup = subgroupCodeResult.value
  }

  return result
})

const { games, loading, loadingInit, hasNextPage, totalCount, loadMore } = useGamesList(payload.value)

const swiperReady = ref(false)

const slidesPerColumn = computed(() => {
  if (props.maxRow) {
    const rows = totalCount.value / props.columns
    return rows < props.maxRow ? Math.floor(rows) || 1 : props.maxRow
  }

  if (props.subgroup) {
    return totalCount.value >= props.subgroup.subgroupLikeSliderSize * props.columns
      ? props.subgroup.subgroupLikeSliderSize
      : Math.floor(totalCount.value / props.columns) || 1
  }

  return 1
})

const showAllLink = computed(() => {
  if (props.allLink) {
    return props.allLink
  }

  if (group.value && subgroupCodeResult.value) {
    return { name: 'GamesBySubGroup', params: { group: group.value, subgroup: subgroupCodeResult.value } }
  }

  return { name: 'GamesByGroup', params: { group: group.value } }
})

const swiperInstance = ref(null)

function onSwiper(swiper) {
  swiperInstance.value = swiper
}

const progress = ref()
function loadGames(swiper) {
  const { activeIndex, slides, params } = swiper
  if (!loading.value && hasNextPage.value && params) {
    const countSlides = slides.length / (params.slidesPerView * slidesPerColumn.value)
    const currentSlide = activeIndex / params.slidesPerView
    if (countSlides - currentSlide < 2) {
      loadMore()
    }
  }
}
function onProgress(swiper, progressValue) {
  progress.value = progressValue
}

function nextSlide() {
  swiperInstance.value.slideNext()
}

function prevSlide() {
  swiperInstance.value.slidePrev()
}

const countSlides = computed(() => {
  if (!swiperInstance.value?.params) {
    return null
  }
  const { slides, params } = swiperInstance.value
  return (slides?.length ?? 0) / (params.slidesPerView * slidesPerColumn.value)
})

const mediaQueries = inject('mediaQueries')
const countForSkeleton = computed(() => {
  const slidesPerColumnData = slidesPerColumn.value || 1

  let rowsColumns = props.columns
  if (mediaQueries.max575) {
    rowsColumns = 2
  } else if (mediaQueries.max767) {
    rowsColumns = 3
  } else if (mediaQueries.max1199) {
    rowsColumns = 4
  }

  return rowsColumns * slidesPerColumnData
})

const swiperOptionComputed = computed(() => {
  const grid = {
    fill: slidesPerColumn.value > 1 ? 'column' : 'row',
    rows: slidesPerColumn.value,
  }

  const swiperOption = {
    spaceBetween: 10,
    slideToClickedSlide: false,
    slidesPerGroup: hasPromoSlot.value ? 1 : 2,
    modules: [Grid],
    grid,
    cssMode: true,
  }

  const breakpoints = () => {
    return {
      576: {
        slidesPerView: hasPromoSlot.value ? 2 : 3,
        slidesPerGroup: hasPromoSlot.value ? 2 : 3,
        modules: [Grid],
        grid,
      },
      768: {
        slidesPerView: hasPromoSlot.value ? 2 : 4,
        slidesPerGroup: hasPromoSlot.value ? 2 : 4,
        modules: [Grid, FreeMode],
        grid,
      },
      1024: {
        slidesPerView: hasPromoSlot.value ? 2 : 4,
        slidesPerGroup: hasPromoSlot.value ? 2 : 4,
        modules: [Grid, FreeMode],
        grid,
        cssMode: false,
      },
      1200: {
        slidesPerView: hasPromoSlot.value ? 3 : props.columns,
        slidesPerGroup: hasPromoSlot.value ? 3 : props.columns,
        modules: [Grid, FreeMode],
        cssMode: false,
        grid,
      },
    }
  }

  return {
    ...swiperOption,
    slidesPerView: hasPromoSlot.value ? 1 : 2,
    breakpoints: breakpoints(),
  }
})

const gamesFiltered = computed(() => {
  if (hasPromoSlot.value) {
    const SIZE = 2
    const data = [...games.value]

    const gamesHandler = (acc, game, index) => {
      if (index === 0) {
        acc[acc.length - 1].push(game)
        return acc
      }

      if (acc[acc.length - 1].length === SIZE || index === 1) {
        acc.push([])
      }

      acc[acc.length - 1].push(game)
      return acc
    }

    return data.reduce(gamesHandler, [[]])
  }

  return games.value
})

const title = computed(() => {
  return props.titleProp ? props.titleProp : props.subgroup?.name ?? ''
})

const image = computed(() => {
  return props.subgroup?.image ? `${BASE_URL}${props.subgroup?.image}` : ''
})

const logoSubgroup = computed(() => {
  if (route.name === 'Home') {
    return 'hot'
  }

  return props.subgroup?.slug
})

watch(
  loadingInit,
  (value) => {
    if (!value) {
      emit('loaded', { totalCount: totalCount.value })
    }
  },
  { immediate: true }
)
const isDesktop = computed(() => {
  return mediaQueries.desktop
})

function scrollToTop() {
  window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
}
</script>

<style lang="scss">
.games-slider-group {
  .swiper-grid-column .swiper-wrapper {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: repeat(v-bind(slidesPerColumn), 1fr);
    row-gap: 10px;
    .swiper-slide {
      margin-top: 0 !important;
      height: auto !important;
    }
  }

  .show-all-mobile {
    @media (min-width: 575px) {
      display: none;
    }
  }

  .promo-game {
    margin-bottom: 10px;
  }

  .games-slider {
    position: relative;

    .groups {
      //position: absolute;
      width: 100%;
      //left: 0;
      //top: 5px;
      &.swiper-css-mode {
        .swiper-wrapper {
          overflow-y: hidden;
        }
      }
    }
  }

  .games-list-skeleton {
    width: 100%;
    transition: 0.3s;

    &--ready {
      opacity: 0;
    }
  }

  .game-col--group {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-right: -10px;
    margin-bottom: -10px;
    .game-card {
      width: calc(50% - 5px);
      margin-bottom: 10px;
      &__promo {
        width: 100%;
      }
    }
  }

  .categories-img {
    width: 24px;
    height: 24px;
    margin-right: 8px;

    @include tab {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
